import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";

import { useCockpitSession } from "@/hooks/use-cockpit-session";
import { LogOut } from "lucide-react";
import { SidebarProvider, SidebarTrigger, SidebarInset } from "@/components/ui/sidebar";
import { CockpitSidebar } from "@/components/cockpit/CockpitSidebar";
import { IdleLogoutGuard } from "@/components/auth/IdleLogoutGuard";
import { cockpitPageTitle } from "@/lib/cockpit-nav";

export const Route = createFileRoute("/_cockpit")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Cockpit – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async ({ location }) => {
    // Dynamischer Import hält supabase-js aus dem öffentlichen Entry-Bundle.
    const { supabase } = await import("@/integrations/supabase/client");
    // Erst Sitzung sicherstellen (erneuert abgelaufene Zugriffstoken selbst),
    // dann prüfen. Ein Netzfehler darf niemand abmelden.
    let token: string | null = null;
    try {
      const { data } = await supabase.auth.getSession();
      token = data.session?.access_token ?? null;
    } catch {
      token = null;
    }
    if (!token) {
      try {
        const { data } = await supabase.auth.refreshSession();
        token = data.session?.access_token ?? null;
      } catch {
        token = null;
      }
    }
    if (!token) {
      throw redirect({
        to: "/cockpit/login",
        search: { redirect: location.href },
      });
    }
    const { data, error } = await supabase.auth.getUser();
    if (error && !/unauthorized|invalid token|jwt/i.test(error.message)) {
      // Technische Störung: angemeldet bleiben, die Shell prüft die Rolle.
      return;
    }
    if (error || !data.user) {
      throw redirect({
        to: "/cockpit/login",
        search: { redirect: location.href },
      });
    }
  },

  component: CockpitShell,
});

function CockpitShell() {
  const session = useCockpitSession();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const handleSignOut = async () => {
    await session.signOut();
    navigate({ to: "/cockpit/login", replace: true });
  };

  if (session.loading) {
    // Skelett statt leerer Fläche: Seitenleiste und Kopfzeile stehen schon,
    // damit die Oberfläche beim Fertigladen nicht springt.
    return (
      <div className="flex min-h-dvh w-full bg-background">
        <div className="hidden w-64 shrink-0 border-r border-border bg-sidebar lg:block">
          {/* Gleiche 56 px wie die Kopfzeile: die Ecke oben links springt nicht. */}
          <div className="flex h-14 items-center border-b border-border px-2">
            <div className="h-6 w-32 animate-pulse rounded bg-secondary" />
          </div>
          <div className="space-y-3 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-9 animate-pulse rounded bg-secondary" />
            ))}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="h-14 border-b border-border" />
          <div className="px-4 py-8 md:px-10 md:py-10">
            <div className="h-10 w-64 animate-pulse rounded bg-secondary" />
            <div className="mt-4 h-4 w-full max-w-xl animate-pulse rounded bg-secondary" />
            <div className="mt-8 h-40 animate-pulse rounded-lg bg-secondary" />
          </div>
          <p className="sr-only" role="status">
            Cockpit wird geladen
          </p>
        </div>
      </div>
    );
  }


  if (session.status === "internal") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-6">
        <div className="max-w-md text-center">
          <h1 className="font-display text-3xl font-extrabold">Kein Cockpit-Zugriff</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Ihr Konto ist ein interner creategy-Zugang. Das Cockpit steht ausschließlich Kundinnen und Kunden zur Verfügung.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <button
              onClick={() => navigate({ to: "/admin", replace: true })}
              className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
            >
              Zum Admin-Bereich
            </button>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-secondary"
            >
              <LogOut className="h-4 w-4" /> Abmelden
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (session.status === "none" || session.status === "anonymous") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-6">
        <div className="max-w-md text-center">
          <h1 className="font-display text-3xl font-extrabold">Kein Zugriff</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Ihr Konto ist nicht für das Cockpit freigeschaltet. Bitte wenden Sie sich an Ihre Ansprechperson bei creategy.
          </p>
          <button
            onClick={handleSignOut}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
          >
            <LogOut className="h-4 w-4" /> Abmelden
          </button>
        </div>
      </div>
    );
  }

  // Ein Einladungs-/Reset-Link erzeugt bereits eine gültige Sitzung. Ohne
  // gesetztes Passwort bleibt das Cockpit gesperrt — sonst hätte ein Konto
  // Zugang, ohne das Onboarding abgeschlossen zu haben.
  if (!session.passwordSet) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background px-6">
        <div className="max-w-md text-center">
          <h1 className="font-display text-3xl font-extrabold">Passwort noch nicht gesetzt</h1>
          <p className="mt-3 font-body text-sm text-muted-foreground">
            Bitte legen Sie zuerst Ihr persönliches Passwort fest. Erst danach steht Ihnen das
            Cockpit vollständig zur Verfügung.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <a
              href="/cockpit/reset-password?flow=invite"
              className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
            >
              Passwort jetzt festlegen
            </a>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-secondary"
            >
              <LogOut className="h-4 w-4" /> Abmelden
            </button>
          </div>
        </div>
      </div>
    );
  }



  // Auf Tabletbreite nimmt die ausgeklappte Navigation zu viel Inhaltsbreite
  // weg, deshalb startet sie dort als Icon-Leiste. Eine vorhandene
  // Nutzerentscheidung (Sidebar-Cookie) hat Vorrang.
  const sidebarCookie =
    typeof document !== "undefined"
      ? document.cookie.split("; ").find((c) => c.startsWith("sidebar_state="))
      : undefined;
  const sidebarDefaultOpen = sidebarCookie
    ? sidebarCookie.split("=")[1] === "true"
    : typeof window === "undefined" || window.innerWidth >= 1280;

  return (
    <SidebarProvider defaultOpen={sidebarDefaultOpen}>
      {/* Automatische Abmeldung nach 60 Minuten Inaktivität (Ticket H2). */}
      <IdleLogoutGuard idleMinutes={60} onTimeout={handleSignOut} />
      <a
        href="#cockpit-inhalt"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-brand-button focus:px-4 focus:py-2 focus:font-display focus:text-sm focus:font-extrabold focus:text-brand-button-foreground"
      >
        Zum Inhalt springen
      </a>
      <div className="flex min-h-dvh w-full overflow-x-clip bg-background">
        <CockpitSidebar onSignOut={handleSignOut} />
        <SidebarInset>
          <header className="sticky top-0 z-30 flex h-14 w-full min-w-0 items-center justify-between gap-2 overflow-hidden border-b border-border bg-background px-4 md:px-6">
            <div className="flex min-w-0 items-center gap-2">
              <SidebarTrigger className="h-11 w-auto gap-2 px-2 text-sm font-semibold">
                <span className="md:hidden">Menü</span>
              </SidebarTrigger>
              {/* Orientierung: Wo bin ich? */}
              <span className="min-w-0 truncate font-display text-sm font-extrabold text-primary">
                {cockpitPageTitle(pathname)}
              </span>
            </div>
            {/* „Abmelden" steht nur hier – die Seitenleiste verweist auf das Konto. */}
            <div className="flex min-w-0 items-center gap-3">
              <span className="hidden max-w-[16rem] truncate text-xs text-muted-foreground sm:inline">
                {session.email}
              </span>
              <button
                onClick={handleSignOut}
                className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-semibold text-foreground hover:bg-secondary"
              >
                <LogOut className="h-4 w-4" aria-hidden /> Abmelden
              </button>
            </div>
          </header>
          <main
            id="cockpit-inhalt"
            className="w-full min-w-0 max-w-full flex-1 overflow-x-clip px-4 py-8 md:px-10 md:py-10"
          >
            <Outlet />
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
